import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../App";

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CYCLE_START = new Date("2026-03-08T00:00:00");

const daysBetween = (date1, date2) => {
	const d1 = new Date(date1);
	const d2 = new Date(date2);
	d1.setHours(12, 0, 0, 0);
	d2.setHours(12, 0, 0, 0);
	return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
};

const getWeekInCycle = (date) => {
	const targetDate =
		date instanceof Date ? new Date(date) : new Date(date + "T00:00:00");
	const diffDays = daysBetween(CYCLE_START, targetDate);
	const adjustedDays = ((diffDays % 14) + 14) % 14;
	return Math.floor(adjustedDays / 7) + 1;
};

const employeeHasRegularDayOff = (employee, date) => {
	if (!employee.daysOff || employee.daysOff.length === 0) return false;
	const dayOfWeek = date.getDay();
	const weekInCycle = getWeekInCycle(date);
	return employee.daysOff.some(
		(d) => d.weekNumber === weekInCycle && d.dayOfWeek === dayOfWeek,
	);
};

// Department/Bench abbreviations for legend
const BENCH_ABBREVIATIONS = {
	Urinalysis: "UA",
	SMU: "SMU",
	Chemistry: "CHEM",
	Hematology: "HEMA",
	"Blood Bank": "BB",
	MicroBiology: "MICRO",
	Microbiology: "MICRO",
};

// Badge colors by department (matched on department name)
const getDeptBadgeStyle = (deptName) => {
	const n = String(deptName || "").toLowerCase();
	if (n.includes("smu") || n.includes("specimen"))
		return { background: "#ede9fe", color: "#5b21b6" }; // purple
	if (n.includes("chem")) return { background: "#e0e7ff", color: "#3730a3" }; // indigo
	if (n.includes("hema")) return { background: "#fce7f3", color: "#9d174d" }; // pink
	if (n.includes("blood")) return { background: "#fecaca", color: "#991b1b" }; // red
	if (n.includes("urin")) return { background: "#ecfccb", color: "#3f6212" }; // lime
	if (n.includes("micro")) return { background: "#d1fae5", color: "#065f46" }; // green
	if (n.includes("admin")) return { background: "#e5e7eb", color: "#374151" }; // gray
	return {};
};

function ScheduleView() {
	const [schedule, setSchedule] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [holidays, setHolidays] = useState([]);
	const [daysOff, setDaysOff] = useState([]);
	const [understaffedDays, setUnderstaffedDays] = useState([]);
	const [currentDate, setCurrentDate] = useState(new Date());
	const [viewMode, setViewMode] = useState("employee"); // 'employee' (Table View) or 'calendar'
	const [loading, setLoading] = useState(true);
	const [generating, setGenerating] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [holidayWarning, setHolidayWarning] = useState("");
	const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);

	useEffect(() => {
		loadData();
	}, [currentDate]);

	const getMonthDates = () => {
		const year = currentDate.getFullYear();
		const month = currentDate.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);

		// Pad start to begin on Sunday
		const startPadding = firstDay.getDay();
		const dates = [];

		for (let i = startPadding - 1; i >= 0; i--) {
			const date = new Date(year, month, -i);
			dates.push({ date, isCurrentMonth: false });
		}

		for (let day = 1; day <= lastDay.getDate(); day++) {
			dates.push({
				date: new Date(year, month, day),
				isCurrentMonth: true,
			});
		}

		// Pad end to complete the week
		const endPadding = (7 - (dates.length % 7)) % 7;
		for (let i = 1; i <= endPadding; i++) {
			dates.push({
				date: new Date(year, month + 1, i),
				isCurrentMonth: false,
			});
		}

		return dates;
	};

	const loadData = async () => {
		try {
			const year = currentDate.getFullYear();
			const month = currentDate.getMonth();
			const startDate = new Date(year, month, 1)
				.toISOString()
				.split("T")[0];
			const endDate = new Date(year, month + 1, 0)
				.toISOString()
				.split("T")[0];

			const [
				scheduleRes,
				deptRes,
				empRes,
				holidayRes,
				daysOffRes,
				understaffedRes,
			] = await Promise.all([
				axios.get(
					`${API_URL}/schedules?startDate=${startDate}&endDate=${endDate}`,
				),
				axios.get(`${API_URL}/departments`),
				axios.get(`${API_URL}/employees`),
				axios.get(
					`${API_URL}/holidays?startDate=${startDate}&endDate=${endDate}`,
				),
				axios
					.get(
						`${API_URL}/days-off?startDate=${startDate}&endDate=${endDate}`,
					)
					.catch(() => ({ data: [] })),
				axios
					.get(
						`${API_URL}/schedules/understaffed?startDate=${startDate}&endDate=${endDate}`,
					)
					.catch(() => ({ data: [] })),
			]);

			setSchedule(scheduleRes.data);
			setDepartments(deptRes.data);
			setEmployees(empRes.data);
			setHolidays(holidayRes.data || []);
			setDaysOff(daysOffRes.data || []);
			setUnderstaffedDays(understaffedRes.data || []);
		} catch (error) {
			setError("Failed to load schedule data");
		} finally {
			setLoading(false);
		}
	};

	const generateSchedule = async () => {
		setShowGenerateConfirm(false);
		setGenerating(true);
		setError("");

		try {
			const year = currentDate.getFullYear();
			const month = currentDate.getMonth();
			const startDate = new Date(year, month, 1)
				.toISOString()
				.split("T")[0];
			const endDate = new Date(year, month + 1, 0)
				.toISOString()
				.split("T")[0];

			const result = await axios.post(`${API_URL}/schedules/generate`, {
				startDate,
				endDate,
			});

			let message = `Schedule generated: ${result.data.totalAssignments} assignments created.`;
			if (result.data.understaffedDays?.length > 0) {
				message += ` ⚠️ ${result.data.understaffedDays.length} understaffed day(s) need overtime coverage.`;
				setUnderstaffedDays(result.data.understaffedDays);
			} else {
				setUnderstaffedDays([]);
			}

			setSuccess(message);
			setHolidayWarning(
				result.data.holidaysMissingLeave?.length > 0
					? `Holiday leaves must be selected first. No holiday leave is set for: ${result.data.holidaysMissingLeave
							.map((d) =>
								new Date(d + "T00:00:00").toLocaleDateString(
									"en-US",
									{ month: "short", day: "numeric" },
								),
							)
							.join(", ")}.`
					: "",
			);
			loadData();
			setTimeout(() => setSuccess(""), 8000);
		} catch (error) {
			setError(
				error.response?.data?.error || "Failed to generate schedule",
			);
		} finally {
			setGenerating(false);
		}
	};

	const getAssignmentsForDate = (date) => {
		const dateStr = date.toISOString().split("T")[0];
		return schedule.filter((s) => s.schedule_date === dateStr);
	};

	const getFloatEmployeesForDate = (date) => {
		const dateStr = date.toISOString().split("T")[0];
		if (holidays.some((holiday) => holiday.holiday_date === dateStr))
			return [];
		const assignedIds = new Set(
			schedule
				.filter(
					(assignment) =>
						assignment.schedule_date === dateStr &&
						assignment.employee_id !== null,
				)
				.map((assignment) => assignment.employee_id),
		);
		return employees.filter(
			(employee) =>
				!assignedIds.has(employee.id) &&
				!daysOff.some(
					(dayOff) =>
						dayOff.employee_id === employee.id &&
						dayOff.day_off_date === dateStr,
				) &&
				!employeeHasRegularDayOff(employee, date),
		);
	};

	const getHolidayForDate = (date) => {
		const dateStr = date.toISOString().split("T")[0];
		return holidays.find((h) => h.holiday_date === dateStr);
	};

	const isUnderstaffedDate = (date) => {
		const dateStr = date.toISOString().split("T")[0];
		return understaffedDays.some((u) => u.date === dateStr);
	};

	const prevMonth = () => {
		setCurrentDate(
			new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
		);
	};

	const nextMonth = () => {
		setCurrentDate(
			new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
		);
	};

	const formatMonthYear = () => {
		return currentDate.toLocaleDateString("en-US", {
			month: "long",
			year: "numeric",
		});
	};

	const isToday = (date) => {
		const today = new Date();
		return date.toDateString() === today.toDateString();
	};

	const isWeekend = (date) => {
		const day = date.getDay();
		return day === 0 || day === 6;
	};

	if (loading) {
		return (
			<div className='loading-container'>
				<div className='spinner'></div>
			</div>
		);
	}

	const monthDates = getMonthDates();

	return (
		<div>
			<div className='page-header no-print'>
				<h1 className='page-title'>Schedule</h1>
				<div className='flex gap-sm'>
					<button
						className='btn btn-accent'
						onClick={() => setShowGenerateConfirm(true)}
						disabled={generating}
					>
						{generating ? "Generating..." : "Generate Schedule"}
					</button>
				</div>
			</div>

			{success && <div className='alert alert-success'>{success}</div>}
			{holidayWarning && (
				<div
					className='alert'
					style={{
						background: "var(--warning-100)",
						borderLeft: "4px solid var(--warning-500)",
						marginBottom: "1rem",
					}}
				>
					⚠️ {holidayWarning}{" "}
					<a
						href='/leave-management?tab=holiday'
						style={{ fontWeight: 600 }}
					>
						Go to Holiday Excused →
					</a>
				</div>
			)}
			{error && <div className='alert alert-error'>{error}</div>}

			{/* Link to OT Management if there are understaffed days */}
			{understaffedDays.length > 0 && (
				<div
					className='alert'
					style={{
						background: "var(--warning-100)",
						borderLeft: "4px solid var(--warning-500)",
						marginBottom: "1rem",
					}}
				>
					⚠️ <strong>{understaffedDays.length}</strong> day(s) need
					overtime coverage.{" "}
					<a href='/overtime' style={{ fontWeight: 600 }}>
						Go to Overtime Management →
					</a>
				</div>
			)}

			<div className='card'>
				<div className='flex justify-between items-center mb-md no-print'>
					<button className='btn btn-secondary' onClick={prevMonth}>
						← Previous
					</button>
					<h2 style={{ margin: 0 }}>{formatMonthYear()}</h2>
					<button className='btn btn-secondary' onClick={nextMonth}>
						Next →
					</button>
				</div>

				<div className='flex gap-sm mb-md no-print'>
					<button
						className={`btn ${viewMode === "employee" ? "btn-primary" : "btn-secondary"}`}
						onClick={() => setViewMode("employee")}
					>
						Table View
					</button>
					<button
						className={`btn ${viewMode === "calendar" ? "btn-primary" : "btn-secondary"}`}
						onClick={() => setViewMode("calendar")}
					>
						Calendar View
					</button>
					{viewMode === "employee" && (
						<button
							className='btn btn-secondary'
							style={{ marginLeft: "auto" }}
							onClick={() => window.print()}
						>
							🖨️ Print Schedule
						</button>
					)}
				</div>

				{viewMode === "calendar" ? (
					<div className='schedule-grid'>
						{DAYS_OF_WEEK.map((day) => (
							<div key={day} className='schedule-header'>
								{day}
							</div>
						))}
						{monthDates.map(({ date, isCurrentMonth }, idx) => {
							const assignments = getAssignmentsForDate(date);
							const holiday = getHolidayForDate(date);
							const understaffed = isUnderstaffedDate(date);
							return (
								<div
									key={idx}
									className={`schedule-cell ${isWeekend(date) ? "weekend" : ""} ${isToday(date) ? "today" : ""} ${understaffed ? "understaffed" : ""}`}
									style={{
										opacity: isCurrentMonth ? 1 : 0.4,
										background: holiday
											? "var(--warning-100)"
											: undefined,
										borderColor: understaffed
											? "var(--danger)"
											: undefined,
									}}
								>
									<div className='schedule-date'>
										{date.getDate()}
									</div>
									{holiday && (
										<div
											className='text-xs'
											style={{
												color: "var(--warning-700)",
												fontWeight: 500,
												marginBottom: "0.25rem",
											}}
										>
											🎉 {holiday.holiday_name}
										</div>
									)}
									{understaffed && (
										<div
											className='text-xs'
											style={{
												color: "var(--danger)",
												fontWeight: 500,
												marginBottom: "0.25rem",
											}}
										>
											⚠️ Needs Overtime
										</div>
									)}
									{assignments.map((a, i) => (
										<div
											key={i}
											className={`schedule-assignment${a.is_overtime ? " ot-assignment" : ""}`}
											title={`${a.first_name} - ${a.bench_name || a.department_name}${a.is_overtime ? " (OT)" : ""}`}
										>
											{a.is_overtime === 1 && (
												<span
													style={{
														color: "var(--warning)",
														fontWeight: 700,
														fontSize: "0.65rem",
														marginRight: 2,
													}}
												>
													OT
												</span>
											)}
											{a.department_name}: {a.first_name}
										</div>
									))}
									{getFloatEmployeesForDate(date).map(
										(employee) => (
											<div
												key={`float-${employee.id}`}
												className='schedule-assignment'
												title={`${employee.first_name} - Float`}
												style={{
													color: "var(--gray-600)",
													border: "1px dashed var(--gray-400)",
												}}
											>
												FLT: {employee.first_name}
											</div>
										),
									)}
								</div>
							);
						})}
					</div>
				) : (
					<EmployeeTableView
						employees={employees}
						departments={departments}
						schedule={schedule}
						daysOff={daysOff}
						holidays={holidays}
						currentDate={currentDate}
					/>
				)}
			</div>

			{showGenerateConfirm && (
				<div
					className='modal-overlay'
					onClick={() => setShowGenerateConfirm(false)}
				>
					<div className='modal' onClick={(e) => e.stopPropagation()}>
						<div className='modal-header'>
							<h3 style={{ margin: 0 }}>Generate Schedule</h3>
							<button
								className='modal-close'
								onClick={() => setShowGenerateConfirm(false)}
								aria-label='Close'
							>
								&times;
							</button>
						</div>
						<div className='modal-body'>
							<p style={{ margin: 0 }}>
								This will regenerate the schedule for{" "}
								<strong>{formatMonthYear()}</strong>. Existing
								assignments for this month will be replaced.
							</p>
							<p
								className='text-muted'
								style={{ marginTop: "0.75rem", marginBottom: 0 }}
							>
								This cannot be undone.
							</p>
						</div>
						<div className='modal-footer'>
							<button
								className='btn btn-secondary'
								onClick={() => setShowGenerateConfirm(false)}
							>
								Cancel
							</button>
							<button
								className='btn btn-accent'
								onClick={generateSchedule}
							>
								Generate Schedule
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

// Employee-centric table view component
function EmployeeTableView({
	employees,
	departments,
	schedule,
	daysOff,
	holidays,
	currentDate,
}) {
	// Calculate pay period number (1-indexed, starting from CYCLE_START)
	const getPayPeriodNumber = (date) => {
		const targetDate =
			date instanceof Date
				? new Date(date)
				: new Date(date + "T00:00:00");
		const cycleStart = new Date(CYCLE_START);
		const diffDays = daysBetween(cycleStart, targetDate);
		return Math.floor(diffDays / 14) + 1;
	};

	// Check if date is start of a new week (Sunday)
	const isWeekStart = (date) => date.getDay() === 0;

	// Check if date is start of a new pay period (Week 1, Sunday)
	const isPayPeriodStart = (date) =>
		isWeekStart(date) && getWeekInCycle(date) === 1;

	// Get days in the month
	const year = currentDate.getFullYear();
	const month = currentDate.getMonth();
	const daysInMonth = new Date(year, month + 1, 0).getDate();

	// Create array of dates for the month
	const monthDates = [];
	for (let d = 1; d <= daysInMonth; d++) {
		monthDates.push(new Date(year, month, d));
	}

	// Get assignment abbreviation
	const getAbbreviation = (deptName, benchName) => {
		if (benchName) {
			// If bench name exists, use first 2-3 chars of bench
			const abbr = benchName.substring(0, 3).toUpperCase();
			return abbr;
		}
		return (
			BENCH_ABBREVIATIONS[deptName] ||
			deptName?.substring(0, 4).toUpperCase() ||
			"?"
		);
	};

	// Check if date is a holiday
	const isHoliday = (date) => {
		const dateStr = date.toISOString().split("T")[0];
		return holidays.find((h) => h.holiday_date === dateStr);
	};

	// Total minimum staff for a day: sum of department minimums (weekend rule on
	// weekends/holidays). A department without an explicit minimum falls back to
	// its bench requirements, same as the scheduler.
	const getDayMinimum = (date) => {
		const weekendLike =
			date.getDay() === 0 || date.getDay() === 6 || !!isHoliday(date);
		return (departments || []).reduce((sum, d) => {
			const benches = d.benches || [];
			const total = (key) =>
				benches.reduce((s, b) => s + (Number(b[key]) || 0), 0);
			if (weekendLike) {
				return (
					sum +
					(d.min_weekend_staff != null
						? Number(d.min_weekend_staff)
						: (total("saturday_staff_needed") +
								total("sunday_staff_needed")) /
							2)
				);
			}
			return (
				sum +
				(d.min_weekday_staff != null
					? Number(d.min_weekday_staff)
					: total("weekday_staff_needed"))
			);
		}, 0);
	};

	// Get day off for employee on date
	const getDayOff = (employeeId, date) => {
		const dateStr = date.toISOString().split("T")[0];
		return daysOff.find(
			(d) => d.employee_id === employeeId && d.day_off_date === dateStr,
		);
	};

	const getDayOffLabel = (dayOff) => {
		if (!dayOff) return "LEAVE";
		const leaveType = String(dayOff.leave_type || "").toLowerCase();
		return leaveType.includes("holiday") ? "HX" : "LEAVE";
	};

	const getDayOffBadgeClass = (dayOff) => {
		return "badge-warning";
	};

	// Get regular (non-OT) assignments for employee on date; OT is hidden in this view
	const getEmployeeAssignments = (employeeId, date) => {
		const dateStr = date.toISOString().split("T")[0];
		return schedule.filter(
			(s) =>
				s.employee_id === employeeId &&
				s.schedule_date === dateStr &&
				s.is_overtime !== 1,
		);
	};

	// Collect unique abbreviations for legend
	const usedAbbreviations = new Map();
	schedule.forEach((s) => {
		if (s.is_overtime === 1 || s.is_candidate_ot) return;
		const abbr = getAbbreviation(s.department_name, s.bench_name);
		let fullName = s.bench_name || s.department_name;
		if (abbr === "SMU") fullName = "SMU";
		if (
			abbr === "CHEM" ||
			String(fullName).toLowerCase().includes("chem")
		) {
			fullName = "Chemistry";
		}
		if (!usedAbbreviations.has(abbr)) {
			usedAbbreviations.set(abbr, {
				fullName,
				deptName: s.department_name,
			});
		}
	});

	// Show supervisors at the top of the employee table.
	const sortedEmployees = [...employees].sort((a, b) => {
		const aIsSupervisor = a.is_supervisor ? 1 : 0;
		const bIsSupervisor = b.is_supervisor ? 1 : 0;
		if (aIsSupervisor !== bIsSupervisor)
			return bIsSupervisor - aIsSupervisor;
		const aName = a.first_name || "";
		const bName = b.first_name || "";
		return aName.localeCompare(bName);
	});

	return (
		<div className='print-area'>
			<h2 className='print-only print-title'>
				<span>Evening Shift Schedule</span>
				<span>
					{currentDate.toLocaleString("en-US", {
						month: "long",
						year: "numeric",
					})}
				</span>
			</h2>
			{/* Legend */}
			<div
				className='schedule-legend'
				style={{
					marginBottom: "1rem",
					padding: "0.75rem",
					background: "var(--gray-50)",
					borderRadius: "4px",
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: "1rem",
					fontSize: "0.85rem",
				}}
			>
				<strong>Legend:</strong>
				{Array.from(usedAbbreviations.entries()).map(
					([abbr, { fullName, deptName }]) => (
						<span key={abbr}>
							<span
								className='badge badge-primary'
								style={{
									marginRight: "4px",
									...getDeptBadgeStyle(deptName),
								}}
							>
								{abbr}
							</span>
							= {fullName}
						</span>
					),
				)}
				<span>
					<span
						className='badge badge-primary'
						style={{ marginRight: "4px" }}
					>
						FLT
					</span>
					= Float (no bench assignment)
				</span>
				<span>
					<span
						style={{
							color: "var(--accent)",
							marginRight: "4px",
						}}
					>
						★
					</span>
					= Supervisor
				</span>
				<span>
					<strong style={{ marginRight: "4px" }}>- T</strong>
					= Trainee
				</span>
				<span>
					<span
					className='badge'
					style={{
						background: "#e3f2fd",
						color: "var(--gray-500)",
						marginRight: "4px",
					}}
				>
					NWD
					</span>
					= Regular Day Off
				</span>
				<span>
					<span
						className='badge badge-warning'
						style={{ marginRight: "4px" }}
					>
						LEAVE
					</span>
					= Annual Leave
				</span>
				<span>
					<span
						className='badge badge-warning'
						style={{ marginRight: "4px" }}
					>
						HX
					</span>
					= Holiday Excused
				</span>
				<span>
					<span
						style={{
							color: "var(--danger)",
							fontWeight: "bold",
							marginRight: "4px",
						}}
					>
						?
					</span>
					= Missing Assignment
				</span>
				<span
					style={{
						marginLeft: "0.5rem",
						borderLeft: "1px solid var(--gray-300)",
						paddingLeft: "1rem",
					}}
				>
					<span
						style={{
							background: "#e3f2fd",
							padding: "2px 6px",
							borderRadius: "3px",
							marginRight: "4px",
							fontSize: "0.75rem",
						}}
					>
						Wk 1
					</span>
					<span
						style={{
							background: "#fff3e0",
							padding: "2px 6px",
							borderRadius: "3px",
							marginRight: "4px",
							fontSize: "0.75rem",
						}}
					>
						Wk 2
					</span>
					= Pay Period Weeks
				</span>
				<span>
					<span
						style={{
							borderLeft: "1px solid #333",
							paddingLeft: "4px",
							marginRight: "4px",
						}}
					>
						|
					</span>
					= Pay Period Start
				</span>
			</div>

			{/* Table */}
			<div className='table-container' style={{ overflowX: "auto" }}>
				<table
					className='table print-table'
					style={{
						fontSize: "0.8rem",
						minWidth: "100%",
						borderCollapse: "separate",
						borderSpacing: 0,
					}}
				>
					<thead>
						<tr>
							<th
								style={{
									position: "sticky",
									left: 0,
									background: "var(--gray-100)",
									zIndex: 2,
									width: "1%",
									whiteSpace: "nowrap",
								}}
							>
								Employee
							</th>
							{monthDates.map((date, idx) => {
								const holiday = isHoliday(date);
								const weekNum = getWeekInCycle(date);
								const ppStart = isPayPeriodStart(date);
								const wkStart = isWeekStart(date) && !ppStart;
								return (
									<th
										key={idx}
										style={{
											textAlign: "center",
											padding: "0.25rem",
											minWidth: "45px",
											background: holiday
												? "var(--warning-100)"
												: weekNum === 1
													? "#e3f2fd"
													: "#fff3e0",
											fontSize: "0.75rem",
											borderLeft: ppStart
												? "1px solid #333"
												: wkStart
													? "1px dashed var(--gray-300)"
													: "none",
										}}
										title={
											holiday
												? `🎉 ${holiday.holiday_name}`
												: undefined
										}
									>
										<div>{date.getDate()}</div>
										<div
											style={{ color: "var(--gray-500)" }}
										>
											{
												[
													"S",
													"M",
													"T",
													"W",
													"T",
													"F",
													"S",
												][date.getDay()]
											}
										</div>
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>
						{employees.length === 0 ? (
							<tr>
								<td
									colSpan={daysInMonth + 1}
									className='text-center text-muted'
									style={{ padding: "2rem" }}
								>
									No employees found.
								</td>
							</tr>
						) : (
							sortedEmployees.map((emp) => (
								<tr key={emp.id}>
									<td
										style={{
											position: "sticky",
											left: 0,
											background: "white",
											zIndex: 1,
											fontWeight: 500,
											whiteSpace: "nowrap",
										}}
									>
										{emp.is_supervisor ? (
											<span
												title='Supervisor'
												style={{
													color: "var(--accent)",
													marginRight: "3px",
												}}
											>
												★
											</span>
										) : null}
										{emp.first_name}
									</td>
									{monthDates.map((date, idx) => {
										const assignments =
											getEmployeeAssignments(
												emp.id,
												date,
											);
										const dayOff = getDayOff(emp.id, date);
										const holiday = isHoliday(date);
										const regularDayOff =
											employeeHasRegularDayOff(emp, date);
										const ppStart = isPayPeriodStart(date);
										const wkStart =
											isWeekStart(date) && !ppStart;

										let cellContent;
										let cellStyle = {
											textAlign: "center",
											padding: "0.25rem",
											verticalAlign: "middle",
											borderLeft: ppStart
												? "1px solid #333"
												: wkStart
													? "1px dashed var(--gray-300)"
													: "none",
										};

										if (dayOff) {
											cellContent = (
												<span
													className={`badge ${getDayOffBadgeClass(dayOff)}`}
													style={{
														fontSize: "0.65rem",
													}}
												>
													{getDayOffLabel(dayOff)}
												</span>
											);
										} else if (assignments.length > 0) {
											// SMU / UA go above Micro when they are paired in one day
											const rank = (a) => {
												const ab = getAbbreviation(
													a.department_name,
													a.bench_name,
												);
												return ab === "SMU"
													? 0
													: ab === "UA"
														? 1
														: 2;
											};
											const ordered = [...assignments].sort(
												(x, y) => rank(x) - rank(y),
											);
											const abbrs = ordered.map((a) =>
												getAbbreviation(
													a.department_name,
													a.bench_name,
												),
											);
											const isMicroPair =
												ordered.length === 2 &&
												abbrs.includes("MIC") &&
												(abbrs.includes("SMU") ||
													abbrs.includes("UA"));
											cellContent = (
												<div
													style={{
														position: "relative",
														display: "flex",
														flexDirection: "column",
														alignItems: "center",
														gap: "1px",
													}}
												>
													{ordered.map((a, i) => (
														<span
															key={i}
															className='badge badge-primary'
															style={{
																fontSize: "0.65rem",
																padding: "2px 5px",
																...getDeptBadgeStyle(
																	a.department_name,
																),
															}}
															title={`${a.department_name}${a.bench_name ? ": " + a.bench_name : ""}${a.is_trainee ? " (Trainee)" : ""}`}
														>
															{getAbbreviation(
																a.department_name,
																a.bench_name,
															)}
															{a.is_trainee ? " - T" : ""}
														</span>
													))}
													{isMicroPair && (
														// Overlaid on the seam so it adds no height
														<span
															aria-hidden='true'
															style={{
																position: "absolute",
																top: "50%",
																left: "50%",
																transform:
																	"translate(-50%, -50%)",
																fontSize: "0.7rem",
																fontWeight: 700,
																lineHeight: 1,
																color: "#64748b",
																opacity: 0.8,
																textShadow:
																	"0 0 2px #fff, 0 0 2px #fff",
																pointerEvents: "none",
															}}
														>
															+
														</span>
													)}
												</div>
											);
										} else if (regularDayOff) {
											// Configured regular day off from profile (2-week cycle)
											cellContent = (
												<span
													className='badge'
													style={{
														background: "#e3f2fd",
														color: "var(--gray-500)",
														fontSize: "0.65rem",
													}}
												>
													NWD
												</span>
											);
										} else {
											// Available employees without a bench assignment are floats.
											cellContent = (
												<span
													className='badge badge-primary'
													title='Float'
													style={{
														fontSize: "0.65rem",
													}}
												>
													FLT
												</span>
											);
										}

										return (
											<td key={idx} style={cellStyle}>
												{cellContent}
											</td>
										);
									})}
								</tr>
							))
						)}
						<tr>
							<td
								style={{
									position: "sticky",
									left: 0,
									background: "var(--gray-100)",
									zIndex: 1,
									fontWeight: 700,
									whiteSpace: "nowrap",
								}}
							>
								Total
							</td>
							{monthDates.map((date, idx) => {
								const dateStr = date
									.toISOString()
									.split("T")[0];
								// Everyone shown as working: a bench badge or FLT (NWD / Leave / HX are
								// not counted). Overtime workers still count.
								const workingIds = new Set();
								employees.forEach((emp) => {
									if (getDayOff(emp.id, date)) return;
									const hasBench =
										getEmployeeAssignments(emp.id, date)
											.length > 0;
									if (
										hasBench ||
										!employeeHasRegularDayOff(emp, date)
									)
										workingIds.add(emp.id);
								});
								schedule
									.filter(
										(s) =>
											s.schedule_date === dateStr &&
											s.is_candidate_ot !== 1,
									)
									.forEach((s) =>
										workingIds.add(s.employee_id),
									);
								const countForDay = workingIds.size;
								const dayMin = getDayMinimum(date);
								const ppStart = isPayPeriodStart(date);
								const wkStart = isWeekStart(date) && !ppStart;
								return (
									<td
										key={idx}
										style={{
											textAlign: "center",
											padding: "0.25rem",
											fontWeight: 700,
											background: "var(--gray-50)",
											borderLeft: ppStart
												? "1px solid #333"
												: wkStart
													? "1px dashed var(--gray-300)"
													: "none",
										}}
									>
										{countForDay}
										<sup
											title='People above the daily minimum'
											style={{
												fontSize: "0.65em",
												marginLeft: "1px",
												color:
													countForDay < dayMin
														? "var(--danger)"
														: "var(--gray-500)",
											}}
										>
											{(() => {
												const diff =
													Math.round(
														(countForDay - dayMin) *
															10,
													) / 10;
												return diff > 0
													? `+${diff}`
													: String(diff);
											})()}
										</sup>
									</td>
								);
							})}
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
}

export { employeeHasRegularDayOff, getDeptBadgeStyle };
export default ScheduleView;
